import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';

import { fadeAnimation, sidepaneMove } from '../../../shared/animations/sidepane-animation';
import { MORTGAGE_DOCUMENT_TYPE, PROTECTION_DOCUMENT_TYPE } from '../../../utils/variables.data';
import { FileControlsConfigBuilder } from '../../../shared/fileuploader/utils/fileuploader.utils';
import { HfValidators } from '../../../utils/form.validators';
import { AddDocumentService } from './add-document.service';
import { FileuploaderComponent } from '../../../shared/fileuploader/fileuploader.component';
import { SelectDocumentTypeService } from '../select-document-type/select-document-type.service';
import { Subscription, Subject } from 'rxjs';
import { JournalTabService } from '../../tabs/journal/journal-tab.service';
import { DocumentsTabService } from '../../tabs/documents/documents-tab.service';
import {
  SelectApplicationLenderService,
  fixHandoverId
} from '../../../modules/mortgages/select-application-lender/select-application-lender.service';
import { delay, filter, takeUntil, tap } from 'rxjs/operators';
import { MortgagesService } from '../../../modules/mortgages/mortgages.service';
import { Store } from '@ngxs/store';
import { ToastService } from '../../../../../../projects/client/src/app/shared/services';

@Component({
  selector: 'hf-add-document',
  templateUrl: 'add-document.component.html',
  styleUrls: ['add-document.component.scss', '../../../styles/components/sidepanes.partial.scss'],
  animations: [sidepaneMove, fadeAnimation],
  host: { '[@sidepaneMove]': 'true' },
  providers: [SelectApplicationLenderService, SelectDocumentTypeService]
})
export class AddDocumentComponent implements AfterViewInit, OnDestroy, OnInit {
  @ViewChild(FileuploaderComponent)
  private fileuploader: FileuploaderComponent;

  public addDocumentForm: UntypedFormGroup;
  public isSubmitting = false;

  public get value() {
    return this.addDocumentForm.value;
  }

  public get handovers() {
    return this.mortgagesService.data?.handovers;
  }

  private documentTypeSelectedSubscription: Subscription;
  private mortgageId;
  private destroy$ = new Subject();

  private control(name: string) {
    return this.addDocumentForm.get(name);
  }

  constructor(
    private fb: UntypedFormBuilder,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private toasterService: ToastService,
    private addDocumentService: AddDocumentService,
    public selectDocumentTypeService: SelectDocumentTypeService,
    private journalTabService: JournalTabService,
    private documentsTabService: DocumentsTabService,
    private selectApplicationLenderService: SelectApplicationLenderService,
    private mortgagesService: MortgagesService,
    private store: Store
  ) {
    this.addDocumentForm = this.fb.group({
      connectedDocument: this.fb.group({
        attachment: this.fb.group(
          new FileControlsConfigBuilder().maxFileSizeDisk().required('Document is required').build()
        ),
        type: [null, Validators.required],
        otherTypeLabel: [{ value: '', disabled: true }, Validators.required],
        sendToClient: ['']
      }),
      internalNotes: [null, Validators.required],
      messageToClient: [{ value: null, disabled: true }, Validators.required],
      handoverId: [null]
    });

    this.addDocumentForm.get('connectedDocument.sendToClient').valueChanges.subscribe((value) => {
      if (value) {
        this.addDocumentForm.get('messageToClient').enable();
        this.addDocumentForm
          .get('connectedDocument.attachment')
          .get(FileControlsConfigBuilder.FILE_SIZE)
          .setValidators([HfValidators.maxFileSizeEmail()]);
      } else {
        this.addDocumentForm.get('messageToClient').disable();
        if (!this.addDocumentForm.get('connectedDocument.attachment').value[FileControlsConfigBuilder.FILE_NAME]) {
          this.addDocumentForm.get('connectedDocument.attachment').reset();
        }
        this.addDocumentForm
          .get('connectedDocument.attachment')
          .get(FileControlsConfigBuilder.FILE_SIZE)
          .setValidators([HfValidators.maxFileSizeDisk()]);
      }
      this.addDocumentForm
        .get('connectedDocument.attachment')
        .get(FileControlsConfigBuilder.FILE_SIZE)
        .updateValueAndValidity();
    });

    this.addDocumentForm.get('connectedDocument.type').valueChanges.subscribe((value: MORTGAGE_DOCUMENT_TYPE) => {
      if (value == 'OTHER') this.addDocumentForm.get('connectedDocument.otherTypeLabel').enable();
      else this.addDocumentForm.get('connectedDocument.otherTypeLabel').disable();
    });

    this.selectApplicationLenderService.application$.pipe(takeUntil(this.destroy$)).subscribe((handover) => {
      this.selectApplicationLenderService.selectedHandover = handover?.id ? handover : null;
      this.control('handoverId').setValue(handover?.id ? handover : null);
    });
  }

  public onSelectLender() {
    this.router.navigate(['selectApplication'], { relativeTo: this.activatedRoute });
  }

  ngOnInit() {
    this.documentTypeSelectedSubscription = this.selectDocumentTypeService.documentTypeChanged.subscribe(
      (type: MORTGAGE_DOCUMENT_TYPE | PROTECTION_DOCUMENT_TYPE) => {
        this.addDocumentForm.get('connectedDocument.type').setValue(type);
      }
    );

    this.mortgageId = this.activatedRoute.snapshot.params['id'];
  }

  ngAfterViewInit() {
    this.documentsTabService.droppedFile$
      .pipe(
        filter((files): files is File[] => !!files && files.length > 0),
        tap(() => this.documentsTabService.droppedFile$.next(null)),
        delay(0),
        takeUntil(this.destroy$)
      )
      .subscribe((files) => this.fileuploader.selectFile(files[0]));
  }

  async onSubmit() {
    const value = fixHandoverId(this.value);

    this.isSubmitting = true;

    try {
      await this.addDocumentService.addDocument(value, this.activatedRoute.snapshot.paramMap);

      const saveAction = this.activatedRoute.snapshot.data.saveAction;
      if (saveAction) {
        this.store.dispatch(new saveAction());
      }

      this.journalTabService.reloadJournalTab.emit();
      this.documentsTabService.reloadDocumentsTab.emit();
      this.toasterService.add({ header: `Document added` }, 'success');
      this.onBack();
    } catch (err) {
      this.toasterService.add({ header: `${err.error.error.message}` }, 'error');
    }
    this.isSubmitting = false;
  }

  public onBack() {
    this.router.navigate(['./..'], { relativeTo: this.activatedRoute });
  }

  ngOnDestroy(): void {
    this.documentTypeSelectedSubscription.unsubscribe();
    this.addDocumentService.selectedDocumentType = null;
    this.destroy$.next();
  }

  selectDocumentType() {
    this.selectDocumentTypeService.selectedDocumentType = this.addDocumentForm.value.connectedDocument.type;
    this.router.navigate(['selectDocumentType'], { relativeTo: this.activatedRoute });
  }
}
